import type TileWMS from 'ol/source/TileWMS';
//import type VectorSource from 'ol/source/Vector';
import VectorLayer from 'ol/layer/Vector';
import type { Layer } from 'ol/layer';
import { getLayerById } from '../layers';
import { getConfigOptional } from '../config';
import { fetchAPI } from '../api';
import type { FilterValue } from './types';
import { getAllFilterValues } from './filter-state';
import type { Style } from 'ol/style';

// ============================================================================
// Типы
// ============================================================================

interface FilterableLayerConfig {
  id: string;
  type: string;
  filterAttributes: Record<string, string>;
}

// ============================================================================
// Получение конфигурации фильтруемых слоёв
// ============================================================================

function getFilterableLayers(): FilterableLayerConfig[] {
  const layersConfig = getConfigOptional('layers');
  if (!layersConfig) return [];

  const result: FilterableLayerConfig[] = [];

  const collectLayers = (groups: any[]) => {
    for (const group of groups) {
      if (group.layers) {
        for (const layer of group.layers) {
          if (layer.filterable && layer.filterAttributes) {
            result.push({
              id: layer.id,
              type: layer.type,
              filterAttributes: layer.filterAttributes
            });
          }
        }
      }
      if (group.groups) {
        collectLayers(group.groups);
      }
    }
  };

  collectLayers(layersConfig.groups);
  return result;
}

// ============================================================================
// Получение эффективного списка ID маршрутов
// ============================================================================

/**
 * Вычисляет список ID маршрутов, удовлетворяющих текущим фильтрам.
 * Если фильтр traverse активен — используем его.
 * Иначе — запрашиваем у сервера список маршрутов по экспедиции/году.
 */
async function getEffectiveTraverseIds(
  filterValues: Record<string, FilterValue>
): Promise<string[] | null> {
  // Если выбраны конкретные маршруты — используем их
  const traverseValue = filterValues['traverse'];
  if (Array.isArray(traverseValue) && traverseValue.length > 0) {
    return traverseValue;
  }

  // Если не выбрана экспедиция — показываем всё (null = без фильтра)
  const expeditionId = filterValues['expedition'];
  if (!expeditionId) {
    return null;
  }

  // Запрашиваем у сервера список маршрутов по фильтрам
  const params: Record<string, string> = {
    owner: String(expeditionId)
  };

  const yearValue = filterValues['year'];
  if (Array.isArray(yearValue) && yearValue.length > 0) {
    params.years = yearValue.join(',');
  }

  try {
    const qs = new URLSearchParams(params).toString();
    const data = await fetchAPI<any>(`/traverses?${qs}`);

    // Извлекаем массив из ответа
    let items: any[];
    if (Array.isArray(data)) {
      items = data;
    } else if (data && Array.isArray(data.data)) {
      items = data.data;
    } else {
      items = [];
    }

    return items.map((item: any) => String(item.id));
  } catch (error) {
    console.error('❌ Ошибка получения эффективных ID маршрутов:', error);
    return null;
  }
}

// ============================================================================
// Построение CQL-фильтра для слоя
// ============================================================================

/**
 * Строит CQL_FILTER строку для конкретного слоя на основе
 * эффективного списка маршрутов и filterAttributes слоя.
 */
function buildCqlForLayer(
  layerConfig: FilterableLayerConfig,
  effectiveTraverseIds: string[] | null,
  filterValues: Record<string, FilterValue>
): string | null {
  const conditions: string[] = [];
  const attrs = layerConfig.filterAttributes;

  // Фильтр по экспедиции
  if (attrs.expedition && filterValues.expedition) {
    conditions.push(`${attrs.expedition} = '${escapeCql(String(filterValues.expedition))}'`);
  }

  // Фильтр по маршрутам (через эффективный список)
  if (attrs.traverse && effectiveTraverseIds !== null) {
    if (effectiveTraverseIds.length === 0) {
      // Нет подходящих маршрутов — скрываем всё
      return '1 = 0';
    }
    const idList = effectiveTraverseIds
      .map(id => `'${escapeCql(id)}'`)
      .join(',');
    conditions.push(`${attrs.traverse} IN (${idList})`);
  }

  if (conditions.length === 0) return null;
  return conditions.join(' AND ');
}

// ============================================================================
// Применение фильтра к слою
// ============================================================================

// Хранилище текущих фильтров для клиентской фильтрации
const activeFilters = {
  expeditionId: null as string | null,
  traverseIds: null as string[] | null
};

function applyCqlToLayer(layer: Layer, cqlFilter: string | null): void {
  const source = layer.getSource();
  if (!source) return;

  const layerId = layer.get('id') as string;

  if ('updateParams' in source && typeof (source as any).updateParams === 'function') {
    // WMS-слой: обновляем параметр CQL_FILTER
    const wmsSource = source as TileWMS;
    if (cqlFilter) {
      wmsSource.updateParams({ CQL_FILTER: cqlFilter });
    } else {
      wmsSource.updateParams({ CQL_FILTER: undefined });
    }
  } else if ('getFeatures' in source) {
    // Векторный слой (WFS/GeoJSON): применяем клиентскую фильтрацию через стиль
    applyClientFilter(layer, layerId);
  }
}

/**
 * Применяет клиентскую фильтрацию к векторному слою через стиль.
 */
function applyClientFilter(layer: Layer, layerId: string): void {

  console.log(`🎨 Применение клиентской фильтрации к слою ${layerId}`);
  console.log(`  Активные фильтры:`, activeFilters);
  
  const layerConfig = getConfigOptional('layers');
  if (!layerConfig) return;

  // Находим конфигурацию слоя
  let layerFilterConfig: any = null;
  const findLayer = (groups: any[]) => {
    for (const group of groups) {
      if (group.layers) {
        const found = group.layers.find((l: any) => l.id === layerId);
        if (found) {
          layerFilterConfig = found;
          return;
        }
      }
      if (group.groups) {
        findLayer(group.groups);
        if (layerFilterConfig) return;
      }
    }
  };
  findLayer(layerConfig.groups);

  if (!layerFilterConfig?.filterAttributes) {
    console.log(`  ⚠️ Нет filterAttributes для слоя ${layerId}`);
    return;
  }

  const filterAttrs = layerFilterConfig.filterAttributes;
  console.log(`  📋 filterAttributes:`, filterAttrs);
  const vectorLayer = layer as VectorLayer<any>;
  
  // Получаем текущий стиль слоя
  const originalStyle = vectorLayer.getStyle();
  console.log(`  🎨 originalStyle:`, typeof originalStyle, originalStyle);
  
  // Если фильтр не активен — восстанавливаем оригинальный стиль
  if (!activeFilters.expeditionId && !activeFilters.traverseIds) {
    console.log(`  🔄 Сброс фильтров — восстанавливаем оригинальный стиль`);
    vectorLayer.setStyle(originalStyle);
    return;
  }

  // Считаем фичи для статистики
  const source = vectorLayer.getSource();
  const features = source?.getFeatures() || [];
  console.log(`  📊 Всего фич в слое: ${features.length}`);

  // Создаём функцию стиля с фильтрацией
  vectorLayer.setStyle((feature: any, resolution: number): Style | Style[] | undefined => {
    // Проверяем, соответствует ли фича фильтру
    if (!matchesFilter(feature, filterAttrs)) {
      return undefined; // Не рендерить фичу
    }

    // Если проходит фильтр — применяем оригинальный стиль
    if (typeof originalStyle === 'function') {
      return originalStyle(feature, resolution) as Style | Style[] | undefined;
    }
    return originalStyle as Style | Style[] | undefined;
  });
}

/**
 * Проверяет, соответствует ли фича текущим фильтрам.
 */
function matchesFilter(feature: any, filterAttrs: Record<string, string>): boolean {
  // Логирование для отладки
//  const featureId = feature.getId?.() || 'unknown';
//  console.log(`🔍 Проверка фичи ${featureId}:`, {
//    filterAttrs,
//    activeFilters,
//    featureProperties: feature.getProperties()
//  });
  
  // Проверка по экспедиции
  if (filterAttrs.expedition && activeFilters.expeditionId) {
    const featureExpeditionId = feature.get(filterAttrs.expedition);
//    console.log(`  📌 Экспедиция: фича=${featureExpeditionId}, фильтр=${activeFilters.expeditionId}`);
    if (featureExpeditionId !== activeFilters.expeditionId) {
//      console.log(`  ❌ Не соответствует экспедиции`);
      return false;
    }
  }

  // Проверка по маршрутам
  if (filterAttrs.traverse && activeFilters.traverseIds) {
    const featureTraverseId = feature.get(filterAttrs.traverse);
//    console.log(`  📌 Маршрут: фича=${featureTraverseId}, фильтр=`, activeFilters.traverseIds);
    if (!activeFilters.traverseIds.includes(featureTraverseId)) {
//      console.log(`  ❌ Не соответствует маршруту`);
      return false;
    }
  }

//  console.log(`  ✅ Фича проходит фильтр`);
  return true;
}

// ============================================================================
// Главная функция: применение всех фильтров
// ============================================================================

let applyTimeout: ReturnType<typeof setTimeout> | null = null;

/**
 * Применяет текущие фильтры ко всем фильтруемым слоям.
 * Использует debouncing, чтобы не отправлять запросы слишком часто.
 */
export function applyFilters(): void {
  if (applyTimeout) clearTimeout(applyTimeout);

  applyTimeout = setTimeout(async () => {
    await doApplyFilters();
    applyTimeout = null;
  }, 300);
}

/**
 * Немедленное применение фильтров (без debounce).
 */
export async function applyFiltersImmediate(): Promise<void> {
  if (applyTimeout) {
    clearTimeout(applyTimeout);
    applyTimeout = null;
  }
  await doApplyFilters();
}

async function doApplyFilters(): Promise<void> {
  console.log('🔍 Применение фильтров...');

  const filterValues = getAllFilterValues();
  const filterableLayers = getFilterableLayers();

  if (filterableLayers.length === 0) return;

  // Сохраняем активные фильтры для клиентской фильтрации
  activeFilters.expeditionId = filterValues.expedition as string | null;
  
  // Вычисляем эффективный список маршрутов
  const effectiveTraverseIds = await getEffectiveTraverseIds(filterValues);
  activeFilters.traverseIds = effectiveTraverseIds;

  console.log('📋 Эффективные маршруты:', effectiveTraverseIds ? `${effectiveTraverseIds.length} шт.` : 'все');

  // Применяем фильтры к каждому слою
  for (const layerConfig of filterableLayers) {
    const layer = getLayerById(layerConfig.id);
    if (!layer) continue;

    const cqlFilter = buildCqlForLayer(layerConfig, effectiveTraverseIds, filterValues);

    console.log(`  📌 Слой "${layerConfig.id}": CQL = ${cqlFilter || 'нет (все данные)'}`);

    applyCqlToLayer(layer, cqlFilter);
  }
}

// ============================================================================
// Сброс фильтров на слоях
// ============================================================================

export function clearFiltersFromLayers(): void {
  const filterableLayers = getFilterableLayers();

  for (const layerConfig of filterableLayers) {
    const layer = getLayerById(layerConfig.id);
    if (!layer) continue;
    applyCqlToLayer(layer, null);
  }
}

/**
 * Экранирование специальных символов для CQL.
 */
function escapeCql(value: string): string {
  return value.replace(/'/g, "''");
}

